import { useEffect, useState } from "react";

import { useAppSelector } from "../../store/hooks";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FiEye, FiEyeOff, FiLock, FiNavigation } from "react-icons/fi";
import asaasLogo from "../../assets/asaas.png";
import autentiqueLogo from "../../assets/autentique.png";
import { obterTipoPlano, estaEmPeriodoDeTeste } from "../../services/seguranca";
import { useHistory } from "react-router-dom";
import { confirm } from "../../components/confirm";
import { FaWhatsapp } from "react-icons/fa";
import AssistenteWhatsAppSecao from "./AssistenteWhatsAppSecao";
type ContaAsaasAnterior = {
    id: string;
    idFilial?: string | null;
    substituidaEm: string;
    verificadoEm?: string | null;
    cobrancasEmAberto?: number | null;
    assinaturasAtivas?: number | null;
    erro?: string | null;
    chaveFinal?: string | null;
};


interface IntegracaoTabProps {
    empresaId: string;
    initialChaveApiAsaas: string;
    initialChaveApiAutentique: string;
    // C-04: o servidor não devolve mais as chaves; só diz se estão configuradas e o final delas.
    initialChaveApiAsaasConfigurada?: boolean;
    initialChaveApiAsaasFinal?: string | null;
    initialChaveApiAutentiqueConfigurada?: boolean;
    initialChaveApiAutentiqueFinal?: string | null;
    initialAutentiqueRemetenteEmail?: string;
    initialAzulMonitorEmail?: string;
    initialAzulMonitorConfigurado?: boolean;
}

export const IntegracaoTab: React.FC<IntegracaoTabProps> = ({
    empresaId,
    initialChaveApiAsaas,
    initialChaveApiAutentique,
    initialChaveApiAsaasConfigurada,
    initialChaveApiAsaasFinal,
    initialChaveApiAutentiqueConfigurada,
    initialChaveApiAutentiqueFinal,
    initialAutentiqueRemetenteEmail,
    initialAzulMonitorEmail,
    initialAzulMonitorConfigurado
}) => {
    const history = useHistory();
    const [activeSubTab, setActiveSubTab] = useState('asaas');
    const [chaveApiAsaas, setChaveApiAsaas] = useState(initialChaveApiAsaas || '');
    const [chaveApiAutentique, setChaveApiAutentique] = useState(initialChaveApiAutentique || '');
    const [asaasConfigurada, setAsaasConfigurada] = useState(!!initialChaveApiAsaasConfigurada || !!initialChaveApiAsaas);
    const [autentiqueConfigurada, setAutentiqueConfigurada] = useState(!!initialChaveApiAutentiqueConfigurada || !!initialChaveApiAutentique);
    const [autentiqueRemetenteEmail, setAutentiqueRemetenteEmail] = useState(initialAutentiqueRemetenteEmail || "");
    const [loadingAsaas, setLoadingAsaas] = useState(false);
    const [loadingAutentique, setLoadingAutentique] = useState(false);
    const [mostrarChaveAsaas, setMostrarChaveAsaas] = useState(false);
    const [mostrarChaveAutentique, setMostrarChaveAutentique] = useState(false);
    const [azulMonitorEmail, setAzulMonitorEmail] = useState(initialAzulMonitorEmail || "");
    const [azulMonitorSenha, setAzulMonitorSenha] = useState("");
    const [azulMonitorConfigurado, setAzulMonitorConfigurado] = useState(!!initialAzulMonitorConfigurado);
    const [loadingAzulMonitor, setLoadingAzulMonitor] = useState(false);
    const [mostrarSenhaAzulMonitor, setMostrarSenhaAzulMonitor] = useState(false);
    const [tipoPlanoAtual, setTipoPlanoAtual] = useState<'PRO' | 'AVANCADO' | null>(null);
    const [contasAsaasAnteriores, setContasAsaasAnteriores] = useState<ContaAsaasAnterior[]>([]);
    const [carregandoPlano, setCarregandoPlano] = useState(true);

    const token = useAppSelector((state) => state.token);

    useEffect(() => {
        const carregarPlano = async () => {
            if (!token) {
                setCarregandoPlano(false);
                return;
            }

            try {
                const plano = await obterTipoPlano(token);
                setTipoPlanoAtual(plano?.toUpperCase() as 'PRO' | 'AVANCADO');
            } catch {
                setTipoPlanoAtual(null);
            } finally {
                setCarregandoPlano(false);
            }
        };

        carregarPlano();
    }, [token]);

    // Contas Asaas que a empresa usou antes de trocar a chave e que ainda têm cobranças em aberto.
    // As cobranças delas continuam sendo recebidas e baixadas; o aviso é para o cliente saber onde
    // o dinheiro entra. Falha na consulta não bloqueia a tela.
    const carregarContasAnteriores = async () => {
        if (!empresaId) return;
        try {
            const resp = await api.get(`/ConfiguracaoEmpresa/ContasAsaasAnteriores/${empresaId}`);
            setContasAsaasAnteriores(Array.isArray(resp.data) ? resp.data : []);
        } catch {
            setContasAsaasAnteriores([]);
        }
    };

    useEffect(() => {
        carregarContasAnteriores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresaId]);

    const contasAnterioresComPendencia = contasAsaasAnteriores.filter(
        (c) => (c.cobrancasEmAberto ?? 0) > 0 || (c.assinaturasAtivas ?? 0) > 0 || !!c.erro
    );

    const temAcesso =
        !carregandoPlano &&
        (tipoPlanoAtual === 'AVANCADO' || estaEmPeriodoDeTeste(token));

    const handleDesabilitarAsaas = async () => {
        if (await confirm("Tem certeza que deseja desabilitar a integração com o Asaas? Isso limpará a chave de API configurada.")) {
            setChaveApiAsaas("");
            setLoadingAsaas(true);
            try {
                await api.put(`/ConfiguracaoEmpresa/AtualizarChaveApiAsaas/${empresaId}`, JSON.stringify(""), {
                    headers: { "Content-Type": "application/json" },
                });
                toast.success('Integração Asaas desabilitada com sucesso!');
                setAsaasConfigurada(false);
                carregarContasAnteriores();
            } catch (error: any) {
                const errorMessage = error.response?.data?.Mensagem || 'Erro ao desabilitar integração Asaas';
                toast.error(errorMessage);
            } finally {
                setLoadingAsaas(false);
            }
        }
    };

    const handleDesabilitarAutentique = async () => {
        if (await confirm("Tem certeza que deseja desabilitar a integração com o Autentique? Isso limpará a chave de API e o e-mail configurados.")) {
            setChaveApiAutentique("");
            setAutentiqueRemetenteEmail("");
            setLoadingAutentique(true);
            try {
                await api.put(`/ConfiguracaoEmpresa/AtualizarAutentiqueApiKey/${empresaId}`, JSON.stringify(""), {
                    headers: { "Content-Type": "application/json" },
                });
                await api.put(`/ConfiguracaoEmpresa/AtualizarAutentiqueRemetenteEmail/${empresaId}`, JSON.stringify(""), {
                    headers: { "Content-Type": "application/json" },
                });
                toast.success('Integração Autentique desabilitada com sucesso!');
                setAutentiqueConfigurada(false);
            } catch (error: any) {
                const errorMessage = error.response?.data?.Mensagem || 'Erro ao desabilitar integração Autentique';
                toast.error(errorMessage);
            } finally {
                setLoadingAutentique(false);
            }
        }
    };

    const handleSaveAsaas = async () => {
        if (!chaveApiAsaas.trim()) {
            toast.warning(asaasConfigurada
                ? 'A chave atual continua configurada. Para trocar, cole a nova chave; para desligar a integração, use "Desabilitar".'
                : 'Cole a chave da API do Asaas antes de salvar.');
            return;
        }
        try {
            setLoadingAsaas(true);
            const resp = await api.put(`/ConfiguracaoEmpresa/AtualizarChaveApiAsaas/${empresaId}`, JSON.stringify(chaveApiAsaas), {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            if (resp.data?.avisoWebhook) {
                // Chave salva, mas o webhook não foi registrado: sem ele os pagamentos dos locatários
                // não chegam ao sistema. Não pode passar como sucesso.
                toast.warning(resp.data.avisoWebhook, { autoClose: false });
            } else {
                toast.success('Chave API Asaas atualizada com sucesso!');
            }
            setAsaasConfigurada(true);
            setChaveApiAsaas('');
            carregarContasAnteriores();
        } catch (error: any) {
            const errorMessage = error.response?.data?.Mensagem || 'Erro ao salvar a chave API Asaas';
            toast.error(errorMessage);
        } finally {
            setLoadingAsaas(false);
        }
    };

    const handleSaveAutentique = async () => {
        // Validação: chave e e-mail andam juntos. Com a chave já configurada no servidor (C-04: ela não
        // volta ao navegador), campo vazio significa "manter a chave atual" e só o e-mail é atualizado.
        const manterChave = !chaveApiAutentique && autentiqueConfigurada;
        if ((chaveApiAutentique && !autentiqueRemetenteEmail) || (!chaveApiAutentique && autentiqueRemetenteEmail && !manterChave)) {
            toast.warning("Para configurar a integração Autentique, é necessário informar tanto a Chave de API quanto o E-mail do Remetente.");
            return;
        }

        setLoadingAutentique(true);
        let success = true;

        if (!manterChave) {
            try {
                // Save Autentique API Key
                await api.put(`/ConfiguracaoEmpresa/AtualizarAutentiqueApiKey/${empresaId}`, JSON.stringify(chaveApiAutentique), {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });
                toast.success('Chave API Autentique atualizada com sucesso!');
                setAutentiqueConfigurada(!!chaveApiAutentique);
                setChaveApiAutentique('');
            } catch (error: any) {
                const errorMessage = error.response?.data?.Mensagem || 'Erro ao salvar a chave API Autentique';
                toast.error(errorMessage);
                success = false;
            }
        }

        try {
            // Save Autentique Sender Email
            await api.put(`/ConfiguracaoEmpresa/AtualizarAutentiqueRemetenteEmail/${empresaId}`, JSON.stringify(autentiqueRemetenteEmail), {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            toast.success('E-mail do remetente Autentique atualizado com sucesso!');
        } catch (error: any) {
            const errorMessage = error.response?.data?.Mensagem || 'Erro ao salvar o e-mail do remetente Autentique';
            toast.error(errorMessage);
            success = false;
        } finally {
            setLoadingAutentique(false);
        }
    };

    const handleSaveAzulMonitor = async (): Promise<boolean> => {
        if (!azulMonitorEmail || !azulMonitorSenha) {
            toast.warning("Para configurar a integração Agillock, é necessário informar tanto o E-mail quanto a Senha.");
            return false;
        }

        setLoadingAzulMonitor(true);
        try {
            await api.put(`/AzulMonitor/AtualizarCredenciais/${empresaId}`, {
                email: azulMonitorEmail,
                senha: azulMonitorSenha
            });
            setAzulMonitorConfigurado(true);
            setAzulMonitorSenha("");
            toast.success('Credenciais Agillock salvas com sucesso!');
            return true;
        } catch (error: any) {
            const errorMessage = error.response?.data?.Mensagem || 'Erro ao salvar credenciais Agillock';
            toast.error(errorMessage);
            return false;
        } finally {
            setLoadingAzulMonitor(false);
        }
    };

    const handleTestarConexaoAzulMonitor = async () => {
        // Se o usuário digitou nova senha, salva antes de testar.
        if (azulMonitorSenha) {
            const salvou = await handleSaveAzulMonitor();
            if (!salvou) return;
        } else if (!azulMonitorConfigurado) {
            toast.warning("Salve suas credenciais antes de testar a conexão.");
            return;
        }

        setLoadingAzulMonitor(true);
        try {
            const response = await api.get(`/AzulMonitor/TestarConexao/${empresaId}`);
            if (response.data === true) {
                toast.success('Conexão com a Agillock realizada com sucesso!');
            } else {
                toast.error('Não foi possível conectar à Agillock. Verifique suas credenciais.');
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.Mensagem || 'Erro ao testar conexão com a Agillock';
            toast.error(errorMessage);
        } finally {
            setLoadingAzulMonitor(false);
        }
    };

    const handleDesabilitarAzulMonitor = async () => {
        if (await confirm("Tem certeza que deseja desabilitar a integração com a Agillock? Isso limpará as credenciais configuradas.")) {
            setLoadingAzulMonitor(true);
            try {
                await api.put(`/AzulMonitor/AtualizarCredenciais/${empresaId}`, {
                    email: "",
                    senha: ""
                });
                setAzulMonitorEmail("");
                setAzulMonitorSenha("");
                setAzulMonitorConfigurado(false);
                toast.success('Integração Agillock desabilitada com sucesso!');
            } catch (error: any) {
                const errorMessage = error.response?.data?.Mensagem || 'Erro ao desabilitar integração Agillock';
                toast.error(errorMessage);
            } finally {
                setLoadingAzulMonitor(false);
            }
        }
    };

    const handleAsaasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChaveApiAsaas(e.target.value);
    };

    const handleAutentiqueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChaveApiAutentique(e.target.value);
    };

    const handleAutentiqueRemetenteEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAutentiqueRemetenteEmail(e.target.value);
    };

    const cartaoPlanoAvancado = (
        <div className="bg-white dark:bg-gray-900 p-8 rounded-lg flex flex-col items-center justify-center text-center space-y-4">
            <div className="bg-blue-100 p-4 rounded-full">
                <FiLock size={48} className="text-[#1F467E]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F467E]">Funcionalidade exclusiva do plano Avançado</h2>
            <p className="text-gray-600 dark:text-gray-500 max-w-md">
                Para acessar todas as nossas integrações, faça um upgrade para o plano Avançado e potencialize sua gestão.
            </p>
            <button
                onClick={() => history.push('/checkout?acao=upgrade&tipo=AVANCADO')}
                className="px-6 py-2 bg-[#1F467E] text-white rounded-md font-medium hover:bg-[#2a5a9e] transition-colors shadow-lg"
            >
                Fazer Upgrade Agora
            </button>
        </div>
    );

    return (
        <div className="bg-white dark:bg-gray-900 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-black dark:text-gray-100 mb-6">Integrações</h2>

            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
                <button
                    className={`px-4 py-2 font-medium text-sm flex items-center whitespace-nowrap ${activeSubTab === 'asaas' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveSubTab('asaas')}
                >
                    <img
                        src={asaasLogo}
                        alt="Asaas"
                        className="w-5 h-5 mr-2"
                    />
                    Asaas
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm flex items-center whitespace-nowrap ${activeSubTab === 'autentique' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveSubTab('autentique')}
                >
                    <img
                        src={autentiqueLogo}
                        alt="Autentique"
                        className="w-5 h-5 mr-2"
                    />
                    Autentique
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm flex items-center whitespace-nowrap ${activeSubTab === 'azulmonitor' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveSubTab('azulmonitor')}
                >
                    <img src="https://agillock.com.br/img/agillock_new_symbol.png" alt="Agillock" style={{ width: 20, height: 20, marginRight: 8, objectFit: 'contain' }} />
                    Agillock
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm flex items-center whitespace-nowrap ${activeSubTab === 'whatsapp' ? 'text-[#1F467E] border-b-2 border-[#1F467E]' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    onClick={() => setActiveSubTab('whatsapp')}
                >
                    <FaWhatsapp className="w-5 h-5 mr-2 text-[#1F467E]" />
                    Assistente WhatsApp
                </button>
            </div>

            {activeSubTab !== 'whatsapp' && !temAcesso && cartaoPlanoAvancado}

            {activeSubTab === 'whatsapp' && <AssistenteWhatsAppSecao />}

            {temAcesso && activeSubTab === 'asaas' && (
                <div className="space-y-6 max-w-3xl">
                    <div>
                        <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3">Integração Asaas</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Configure a chave da API do Asaas para integração com o sistema de pagamentos.
                        </p>

                        {contasAnterioresComPendencia.length > 0 && (
                            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 text-sm text-amber-900 dark:text-amber-200">
                                <p className="font-medium mb-1">Você ainda recebe pela conta Asaas anterior</p>
                                <p className="mb-2">
                                    Ao trocar a chave, as cobranças e assinaturas já criadas continuam na conta antiga. O sistema
                                    segue acompanhando essa conta: os pagamentos entram lá e são baixados normalmente. Cobranças novas
                                    vão para a conta atual.
                                </p>
                                <ul className="list-disc pl-5 space-y-1">
                                    {contasAnterioresComPendencia.map((c) => (
                                        <li key={c.id}>
                                            Conta{c.chaveFinal ? ` …${c.chaveFinal}` : ''}{c.idFilial ? ' (unidade)' : ''}, trocada em{' '}
                                            {new Date(c.substituidaEm).toLocaleDateString('pt-BR')}:{' '}
                                            {c.erro
                                                ? <span>não foi possível consultar ({c.erro})</span>
                                                : <span>{c.cobrancasEmAberto ?? 0} cobrança(s) em aberto, {c.assinaturasAtivas ?? 0} assinatura(s) ativa(s)</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-black dark:text-gray-200 mb-1">
                                    Chave API Asaas
                                </label>

                                <div className="relative">
                                    <input
                                        type={mostrarChaveAsaas ? "text" : "password"}
                                        value={chaveApiAsaas}
                                        onChange={handleAsaasChange}
                                        className="w-full py-2 pl-3 pr-10 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
                                        placeholder={asaasConfigurada ? `Chave configurada${initialChaveApiAsaasFinal ? ` (final …${initialChaveApiAsaasFinal})` : ''}. Cole uma nova chave para trocar.` : "Digite a chave da API do Asaas"}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setMostrarChaveAsaas(!mostrarChaveAsaas)}
                                        className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                                    >
                                        {mostrarChaveAsaas ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                    </button>
                                </div>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    A chave API será salva de forma segura em nosso sistema.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap justify-end gap-2">
                        <div className="w-56">
                            <button
                                onClick={handleSaveAsaas}
                                disabled={loadingAsaas}
                                className="w-full px-3 py-2 bg-[#1F467E] text-white rounded-md text-sm 
                        hover:bg-[#2a5a9e] transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-[#1F467E]
                        disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loadingAsaas ? 'Salvando...' : 'Salvar Chave API Asaas'}
                            </button>
                        </div>

                        <div className="w-56">
                            <button
                                onClick={() => window.open('https://youtu.be/65OEnGYllMY', '_blank')}
                                className="w-full px-3 py-2 bg-[#1F467E] text-white rounded-md text-sm 
                        hover:bg-[#2a5a9e] transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-[#1F467E]"
                            >
                                Entenda como Funciona
                            </button>
                        </div>

                        <div className="w-56">
                            <button
                                onClick={() => window.open('https://www.asaas.com/r/f2824999-6d8e-41ba-b340-df13b4719bf5', '_blank')}
                                className="w-full px-3 py-2 bg-[#1F467E] text-white rounded-md text-sm 
                        hover:bg-[#2a5a9e] transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-[#1F467E]"
                            >
                                Quero criar conta no Asaas
                            </button>
                        </div>

                        <div className="w-56">
                            <button
                                onClick={handleDesabilitarAsaas}
                                disabled={loadingAsaas || !chaveApiAsaas}
                                className="w-full px-3 py-2 bg-red-600 text-white rounded-md text-sm 
                        hover:bg-red-700 transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-red-600
                        disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                Desabilitar Integração
                            </button>
                        </div>
                    </div>

                </div>
            )}

            {temAcesso && activeSubTab === 'autentique' && (
                <div className="space-y-6 max-w-3xl">
                    <div>
                        <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3">Integração Autentique</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Configure a chave da API do Autentique para integração com o sistema de assinaturas de documentos.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-black dark:text-gray-200 mb-1">
                                    Chave API Autentique
                                </label>

                                <div className="relative">
                                    <input
                                        type={mostrarChaveAutentique ? "text" : "password"}
                                        value={chaveApiAutentique}
                                        onChange={handleAutentiqueChange}
                                        className="w-full py-2 pl-3 pr-10 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
                                        placeholder={autentiqueConfigurada ? `Chave configurada${initialChaveApiAutentiqueFinal ? ` (final …${initialChaveApiAutentiqueFinal})` : ''}. Cole uma nova chave para trocar.` : "Digite a chave da API do Autentique"}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setMostrarChaveAutentique(!mostrarChaveAutentique)}
                                        className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                                    >
                                        {mostrarChaveAutentique ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                    </button>
                                </div>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    A chave API será salva de forma segura em nosso sistema.
                                </p>
                            </div>

                            {/* Input para Remetente Email */}
                            <div>
                                <label className="block text-sm font-medium text-black dark:text-gray-200 mb-1">
                                    E-mail do Remetente (Autentique)
                                </label>
                                <input
                                    type="email"
                                    value={autentiqueRemetenteEmail}
                                    onChange={handleAutentiqueRemetenteEmailChange}
                                    className="w-full py-2 pl-3 pr-10 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
                                    placeholder="E-mail vinculado à conta Autentique"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Este e-mail será usado para enviar documentos para assinatura via Autentique. Deve ser o e-mail do titular da conta Autentique.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap justify-end gap-2">
                        <div className="w-56">
                            <button
                                onClick={handleSaveAutentique}
                                disabled={loadingAutentique}
                                className="w-full px-3 py-2 bg-[#1F467E] text-white rounded-md text-sm 
                        hover:bg-[#2a5a9e] transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-[#1F467E]
                        disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loadingAutentique ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>

                        {/* Novo botão de Entenda como funciona */}
                        <div className="w-56">
                            <button
                                onClick={() => window.open('https://youtu.be/iy3TRzH14rU', '_blank')}
                                className="w-full px-3 py-2 bg-[#1F467E] text-white rounded-md text-sm 
                        hover:bg-[#2a5a9e] transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-[#1F467E]"
                            >
                                Entenda como Funciona
                            </button>
                        </div>

                        <div className="w-56">
                            <button
                                onClick={handleDesabilitarAutentique}
                                disabled={loadingAutentique || (!chaveApiAutentique && !autentiqueRemetenteEmail)}
                                className="w-full px-3 py-2 bg-red-600 text-white rounded-md text-sm 
                        hover:bg-red-700 transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-red-600
                        disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                Desabilitar Integração
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {temAcesso && activeSubTab === 'azulmonitor' && (
                <div className="space-y-6 max-w-3xl">
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <img src="https://agillock.com.br/img/agillock_new_symbol.png" alt="Agillock" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                            <h3 className="text-lg font-medium text-black dark:text-gray-100">Integração Agillock</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Configure as credenciais da Agillock para integração com o sistema de rastreamento GPS dos veículos.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-black dark:text-gray-200 mb-1">
                                    E-mail
                                </label>
                                <input
                                    type="email"
                                    value={azulMonitorEmail}
                                    onChange={(e) => setAzulMonitorEmail(e.target.value)}
                                    className="w-full py-2 pl-3 pr-10 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
                                    placeholder="E-mail de acesso à Agillock"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-black dark:text-gray-200 mb-1">
                                    Senha
                                </label>
                                <div className="relative">
                                    <input
                                        type={mostrarSenhaAzulMonitor ? "text" : "password"}
                                        value={azulMonitorSenha}
                                        onChange={(e) => setAzulMonitorSenha(e.target.value)}
                                        className="w-full py-2 pl-3 pr-10 border border-gray-300 rounded-md bg-white dark:bg-gray-800 text-black dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:border-transparent"
                                        placeholder={azulMonitorConfigurado ? "Senha já configurada — preencha para alterar" : "Senha de acesso à Agillock"}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setMostrarSenhaAzulMonitor(!mostrarSenhaAzulMonitor)}
                                        className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                                    >
                                        {mostrarSenhaAzulMonitor ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    As credenciais são armazenadas criptografadas e nunca são exibidas de volta.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap justify-end gap-2">
                        <div className="w-56">
                            <button
                                onClick={handleSaveAzulMonitor}
                                disabled={loadingAzulMonitor}
                                className="w-full px-3 py-2 bg-[#1F467E] text-white rounded-md text-sm
                        hover:bg-[#2a5a9e] transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-[#1F467E]
                        disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loadingAzulMonitor ? 'Salvando...' : 'Salvar Credenciais'}
                            </button>
                        </div>

                        <div className="w-56">
                            <button
                                onClick={handleTestarConexaoAzulMonitor}
                                disabled={loadingAzulMonitor || (!azulMonitorConfigurado && (!azulMonitorEmail || !azulMonitorSenha))}
                                className="w-full px-3 py-2 bg-green-600 text-white rounded-md text-sm
                        hover:bg-green-700 transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-green-600
                        disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loadingAzulMonitor ? 'Testando...' : 'Testar Conexão'}
                            </button>
                        </div>

                        <div className="w-56">
                            <button
                                onClick={handleDesabilitarAzulMonitor}
                                disabled={loadingAzulMonitor || !azulMonitorConfigurado}
                                className="w-full px-3 py-2 bg-red-600 text-white rounded-md text-sm
                        hover:bg-red-700 transition-all shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-red-600
                        disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                Desabilitar Integração
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};